import TrendChart from './TrendChart';
import { getValueHistory } from '../../utils/storage';

export default function MarketValue({ data, analyzedAt, onRefresh, onUpdateValues, updatingValues, vehicleId, serviceLogCount = 0 }) {
  const trendIcon =
    data.trend === 'appreciating' ? '↑' : data.trend === 'stable' ? '→' : '↓';
  const trendColor =
    data.trend === 'declining' ? 'var(--warning)' : 'var(--success)';

  // Calculate staleness
  const daysSinceAnalysis = analyzedAt
    ? Math.floor((Date.now() - new Date(analyzedAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const isStale = daysSinceAnalysis != null && daysSinceAnalysis > 30;

  return (
    <div
      className="card"
      style={{
        marginTop: 12,
        ...(isStale ? { border: '1px solid rgba(245, 158, 11, 0.3)' } : {}),
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h3
            style={{
              fontSize: '0.8125rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 4,
            }}
          >
            Estimated Market Value
          </h3>
          <span style={{ fontSize: '1.75rem', fontWeight: 700 }}>{data.estimate}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              color: trendColor,
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            <span>{trendIcon}</span>
            <span style={{ textTransform: 'capitalize' }}>{data.trend}</span>
          </div>
          {onUpdateValues && (
            <button
              onClick={onUpdateValues}
              disabled={updatingValues}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                padding: '4px 10px',
                fontSize: '0.6875rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid rgba(211, 71, 74, 0.3)',
                background: 'transparent',
                color: 'var(--accent)',
                cursor: updatingValues ? 'not-allowed' : 'pointer',
                opacity: updatingValues ? 0.6 : 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              {updatingValues ? (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Updating…
                </>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10" />
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                  </svg>
                  Update
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Stale data indicator */}
      {isStale && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            marginTop: 8,
            padding: '8px 10px',
            borderRadius: 'var(--radius-sm)',
            background: 'rgba(245, 158, 11, 0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <polyline points="23 4 23 10 17 10" />
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            <span style={{ fontSize: '0.75rem', color: 'var(--warning)' }}>
              Last updated {daysSinceAnalysis} days ago
            </span>
          </div>
          {onRefresh && (
            <button
              onClick={onRefresh}
              style={{
                padding: '4px 10px',
                fontSize: '0.6875rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-sm)',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                background: 'transparent',
                color: 'var(--warning)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              Refresh
            </button>
          )}
        </div>
      )}

      {/* "Last updated" for non-stale (subtle) */}
      {daysSinceAnalysis != null && !isStale && (
        <p style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', marginTop: 4, opacity: 0.6 }}>
          Updated {daysSinceAnalysis === 0 ? 'today' : daysSinceAnalysis === 1 ? 'yesterday' : `${daysSinceAnalysis} days ago`}
        </p>
      )}

      <p
        style={{
          fontSize: '0.8125rem',
          color: 'var(--text-muted)',
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        {data.trendNote}
      </p>

      {/* Empty service log nudge */}
      {serviceLogCount === 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginTop: 10,
            padding: '10px 12px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--bg-elevated)',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', fontWeight: 600, lineHeight: 1.3 }}>
              Add service records for a more accurate valuation
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3, lineHeight: 1.4 }}>
              Completed maintenance like timing belts and major services can increase your vehicle's value.
            </div>
          </div>
        </div>
      )}

      {/* Service Impact on Value */}
      {data.serviceImpact && data.serviceImpact.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 8,
            }}
          >
            Service Impact on Value
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.serviceImpact.map((item, i) => {
              const isCompleted = item.status === 'completed';
              const isNotFound = item.status === 'not_found';
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    padding: '8px 10px',
                    borderRadius: 'var(--radius-sm)',
                    background: isCompleted
                      ? 'rgba(34, 197, 94, 0.06)'
                      : isNotFound
                        ? 'rgba(245, 158, 11, 0.06)'
                        : 'var(--bg-elevated)',
                  }}
                >
                  {isCompleted ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : isNotFound ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  ) : (
                    <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }}>?</span>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      color: isCompleted ? 'var(--success)' : isNotFound ? 'var(--warning)' : 'var(--text-secondary)',
                      lineHeight: 1.3,
                    }}>
                      {item.service}
                    </div>
                    {item.impact && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>
                        {item.impact}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.recentSales && data.recentSales.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <h4
            style={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: 'var(--text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: 8,
            }}
          >
            Recent Comparable Sales
          </h4>
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {data.recentSales.map((sale, i) => (
              <li
                key={i}
                style={{
                  fontSize: '0.8125rem',
                  color: 'var(--text-primary)',
                  lineHeight: 1.4,
                  paddingLeft: 12,
                  borderLeft: '2px solid var(--accent)',
                }}
              >
                {sale}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data.valueFactors && (
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
            marginTop: 12,
            lineHeight: 1.5,
            fontStyle: 'italic',
          }}
        >
          {data.valueFactors}
        </p>
      )}

      {data.dataSource && (
        <p
          style={{
            fontSize: '0.6875rem',
            color: 'var(--text-muted)',
            marginTop: 8,
            opacity: 0.7,
          }}
        >
          {data.dataSource}
        </p>
      )}

      {/* Value trend chart — dual lines for high/low range */}
      {vehicleId && (() => {
        const history = getValueHistory(vehicleId)
          .map((h) => ({
            date: h.date,
            low: h.marketValueLow,
            high: h.marketValueHigh,
            // Fallback for old single-value snapshots
            ...(h.marketValue && !h.marketValueLow ? { low: h.marketValue, high: h.marketValue } : {}),
          }))
          .filter((h) => typeof h.low === 'number' && typeof h.high === 'number');
        return history.length >= 2 ? (
          <TrendChart
            data={history}
            color="var(--warning)"
            colorHigh="var(--success)"
            label="Value History"
            formatValue={(v) => `$${(v / 1000).toFixed(0)}k`}
            rangeMode
            height={120}
          />
        ) : null;
      })()}
    </div>
  );
}
